import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  GalleryItemDto,
  PublicAvailabilityResponse,
  PublicBookingCreatedResponse,
  PublicEmployeeItem,
  PublicServiceItem,
} from '@crm/shared';
import { GalleryLightbox } from '../gallery/GalleryLightbox';
import { assetUrl } from '../../lib/assetUrl';
import { PublicLayout } from './PublicLayout';
import { DatePickerPopover } from './DatePickerPopover';
import {
  createBooking,
  fetchAvailability,
  fetchEmployees,
  fetchPublicGallery,
  fetchServices,
  publicErrorMessage,
} from './api';

type Step = 'services' | 'employee' | 'slot' | 'details' | 'success';

type Selection = {
  serviceIds: number[];
  // null = "dowolny pracownik"; number = konkretny.
  employeeId: number | null;
  // pełen ISO datetime z chosen slot.
  startAt: string | null;
  // employee z slotu (backend zwraca konkretnego, nawet dla "Dowolny").
  resolvedEmployee: { id: number; firstName: string; lastName: string } | null;
};

const initialSelection: Selection = {
  serviceIds: [],
  employeeId: null,
  startAt: null,
  resolvedEmployee: null,
};

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDuration(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}min`;
}

function formatPrice(price: string): string {
  return price.replace(/\.00$/, '');
}

function formatPriceWithCurrency(price: string): string {
  return `${formatPrice(price)} zł`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------
// Główny komponent
// ---------------------------------------------------------------------

export function BookingWizard() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('services');
  const [selection, setSelection] = useState<Selection>(initialSelection);
  const [bookingResult, setBookingResult] = useState<PublicBookingCreatedResponse | null>(null);

  // Reset kreatora przy każdym kliknięciu <Link to="/rezerwacja"> (np. logo
  // w headerze). React Router v6 generuje świeży `location.key` nawet gdy
  // ścieżka się nie zmienia, więc klient w kroku 3 klika logo → wraca na
  // krok 1 z czystym wyborem.
  const location = useLocation();
  useEffect(() => {
    setStep('services');
    setSelection(initialSelection);
    setBookingResult(null);
  }, [location.key]);

  return (
    <PublicLayout>
      {step !== 'success' ? (
        <Stepper current={step} />
      ) : null}

      {step === 'services' ? (
        <StepServices
          selection={selection}
          onNext={(s) => {
            setSelection(s);
            setStep('employee');
          }}
        />
      ) : null}

      {step === 'employee' ? (
        <StepEmployee
          selection={selection}
          onBack={() => setStep('services')}
          onNext={(s) => {
            setSelection(s);
            setStep('slot');
          }}
        />
      ) : null}

      {step === 'slot' ? (
        <StepSlot
          selection={selection}
          onBack={() => setStep('employee')}
          onNext={(s) => {
            setSelection(s);
            setStep('details');
          }}
        />
      ) : null}

      {step === 'details' ? (
        <StepDetails
          selection={selection}
          onBack={() => setStep('slot')}
          onSuccess={(res) => {
            setBookingResult(res);
            setStep('success');
          }}
        />
      ) : null}

      {step === 'success' && bookingResult ? (
        <StepSuccess
          result={bookingResult}
          onNew={() => {
            setSelection(initialSelection);
            setBookingResult(null);
            setStep('services');
          }}
        />
      ) : null}

      {step === 'success' && !bookingResult ? <p>{t('publicBooking.errorGeneric')}</p> : null}
    </PublicLayout>
  );
}

// ---------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------

const STEP_ORDER: Step[] = ['services', 'employee', 'slot', 'details'];

function Stepper({ current }: { current: Step }) {
  const { t } = useTranslation();
  const idx = STEP_ORDER.indexOf(current);
  return (
    <ol className="mb-6 flex flex-wrap items-center gap-2 text-sm">
      {STEP_ORDER.map((s, i) => (
        <li key={s} className="flex items-center gap-2">
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium ${
              i < idx
                ? 'bg-brand-600 text-white'
                : i === idx
                  ? 'bg-brand-100 text-brand-700 ring-2 ring-brand-500 dark:bg-brand-900/40 dark:text-brand-200'
                  : 'bg-zinc-200 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
            }`}
          >
            {i + 1}
          </span>
          <span className={i <= idx ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}>
            {t(`publicBooking.steps.${s}`)}
          </span>
          {i < STEP_ORDER.length - 1 ? <span className="text-zinc-300 dark:text-zinc-600">›</span> : null}
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------
// Krok 1 — usługi
// ---------------------------------------------------------------------

function StepServices({
  selection,
  onNext,
}: {
  selection: Selection;
  onNext: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['public', 'services'],
    queryFn: fetchServices,
  });

  const [picked, setPicked] = useState<number[]>(selection.serviceIds);

  const grouped = useMemo(() => {
    const map = new Map<string, PublicServiceItem[]>();
    for (const s of data ?? []) {
      const rawCat = s.category ?? '—';
      const arr = map.get(rawCat) ?? [];
      arr.push(s);
      map.set(rawCat, arr);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'pl'))
      .map(([rawCat, items]) => [rawCat, items] as const);
  }, [data]);

  const summary = useMemo(() => {
    const items = (data ?? []).filter((s) => picked.includes(s.id));
    const duration = items.reduce((acc, s) => acc + s.durationMinutes, 0);
    const price = items.reduce((acc, s) => acc + Number(s.price), 0);
    return { count: items.length, duration, price: price.toFixed(2) };
  }, [data, picked]);

  if (isLoading) return <p className="dark:text-zinc-300">{t('publicBooking.loading')}</p>;
  if (error) return <p className="text-red-600 dark:text-red-400">{publicErrorMessage(error, '')}</p>;

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
          {t('publicBooking.services.title')}
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.services.subtitle')}</p>
      </header>

      {grouped.length === 0 ? (
        <p className="rounded-lg bg-white p-4 text-zinc-500 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-400 dark:ring-zinc-800">
          {t('publicBooking.services.empty')}
        </p>
      ) : (
        grouped.map(([cat, items]) => (
          <div key={cat} className="rounded-lg bg-white p-4 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800">
            <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              {cat}
            </h2>
            <ul className="space-y-2">
              {items.map((s) => {
                const checked = picked.includes(s.id);
                return (
                  <li key={s.id}>
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-md p-2 transition ${
                        checked
                          ? 'bg-brand-50 ring-1 ring-brand-300 dark:bg-brand-900/30 dark:ring-brand-700'
                          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-zinc-600 dark:bg-zinc-800"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) setPicked([...picked, s.id]);
                          else setPicked(picked.filter((x) => x !== s.id));
                        }}
                      />
                      <div className="flex min-w-0 flex-1 items-start gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">{s.name}</div>
                          {s.description ? (
                            <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                              {s.description}
                            </div>
                          ) : null}
                        </div>
                        <div className="shrink-0 text-right tabular-nums">
                          <div className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                            {formatPriceWithCurrency(s.price)}
                          </div>
                          <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                            {formatDuration(s.durationMinutes)}
                          </div>
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-4 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800">
        <span className="text-sm text-zinc-700 dark:text-zinc-200">
          {t('publicBooking.services.selectedSummary', {
            n: summary.count,
            duration: summary.duration,
            price: formatPrice(summary.price),
          })}
        </span>
        <button
          type="button"
          disabled={picked.length === 0}
          onClick={() => onNext({ ...selection, serviceIds: picked, employeeId: null, startAt: null, resolvedEmployee: null })}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-zinc-300 dark:disabled:bg-zinc-700 dark:disabled:text-zinc-500"
        >
          {t('publicBooking.next')}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------
// Krok 2 — pracownik
// ---------------------------------------------------------------------

function StepEmployee({
  selection,
  onBack,
  onNext,
}: {
  selection: Selection;
  onBack: () => void;
  onNext: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['public', 'employees', selection.serviceIds],
    queryFn: () => fetchEmployees(selection.serviceIds),
    enabled: selection.serviceIds.length > 0,
  });

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
          {t('publicBooking.employee.title')}
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.employee.subtitle')}</p>
      </header>

      {isLoading ? <p className="dark:text-zinc-300">{t('publicBooking.loading')}</p> : null}
      {error ? <p className="text-red-600 dark:text-red-400">{publicErrorMessage(error, '')}</p> : null}

      {data && data.length === 0 ? (
        <p className="rounded-lg bg-white p-4 text-zinc-500 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-400 dark:ring-zinc-800">
          {t('publicBooking.employee.empty')}
        </p>
      ) : null}

      {data && data.length > 0 ? (
        <ul className="space-y-2">
          <li>
            <EmployeeCard
              isAny
              checked={selection.employeeId === null}
              onPick={() => onNext({ ...selection, employeeId: null, startAt: null, resolvedEmployee: null })}
            />
          </li>
          {data.map((e) => (
            <li key={e.id}>
              <EmployeeCard
                employee={e}
                serviceIds={selection.serviceIds}
                checked={selection.employeeId === e.id}
                onPick={() => onNext({ ...selection, employeeId: e.id, startAt: null, resolvedEmployee: null })}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-white px-4 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 transition hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
        >
          {t('publicBooking.back')}
        </button>
      </div>
    </section>
  );
}

function EmployeeCard({
  employee,
  serviceIds,
  isAny = false,
  checked,
  onPick,
}: {
  employee?: PublicEmployeeItem;
  serviceIds?: number[];
  isAny?: boolean;
  checked: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const [lightboxStart, setLightboxStart] = useState<number | null>(null);

  // Galeria pracownika — filtrowana po wybranych usługach (zdjęcia z tagiem
  // jednej z usług + ogólne bez tagu). Tylko dla konkretnego pracownika.
  const gallery = useQuery<GalleryItemDto[]>({
    queryKey: ['public', 'gallery', employee?.id, serviceIds],
    queryFn: () => fetchPublicGallery({ userId: employee!.id, serviceIds }),
    enabled: !isAny && !!employee,
    staleTime: 5 * 60_000,
  });

  // Zdjęcia bez tagu usługi traktujemy jak „ogólne / profilowe" (np. portret pracownika)
  // i przesuwamy na początek — żeby klient w pierwszej kolejności zobaczył twarz pracownika,
  // a dopiero potem realizacje konkretnych usług. Stable sort zachowuje resztę kolejności.
  const items = (gallery.data ?? []).slice().sort((a, b) => {
    const aProfile = a.service ? 1 : 0;
    const bProfile = b.service ? 1 : 0;
    return aProfile - bProfile;
  });
  const previewCount = 3;
  const visible = items.slice(0, previewCount);
  const more = Math.max(0, items.length - previewCount);

  const openLightbox = (e: React.MouseEvent, startIndex: number) => {
    e.stopPropagation();
    setLightboxStart(startIndex);
  };

  return (
    <div
      className={`group w-full rounded-lg p-4 transition ${
        checked
          ? 'bg-brand-50 ring-2 ring-brand-500 dark:bg-brand-900/30 dark:ring-brand-500'
          : 'bg-white ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800'
      }`}
    >
      <button
        type="button"
        onClick={onPick}
        className="flex w-full cursor-pointer items-start gap-3 text-left"
      >
        <span
          className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium text-white transition group-hover:scale-110 group-hover:ring-2 group-hover:ring-brand-400 group-hover:ring-offset-2 dark:group-hover:ring-offset-zinc-900"
          style={{ backgroundColor: isAny ? '#a3a3a3' : (employee?.color ?? '#ec4899') }}
        >
          {isAny ? '?' : `${employee!.firstName[0] ?? ''}${employee!.lastName[0] ?? ''}`}
        </span>
        <div className="flex-1">
          <div
            className={`font-medium text-zinc-900 transition dark:text-zinc-100 ${
              checked ? '' : 'group-hover:font-semibold group-hover:text-brand-700 dark:group-hover:text-brand-300'
            }`}
          >
            {isAny ? t('publicBooking.employee.any') : `${employee!.firstName} ${employee!.lastName}`}
          </div>
          <div
            className={`text-sm text-zinc-500 transition dark:text-zinc-400 ${
              checked ? '' : 'group-hover:text-brand-600 dark:group-hover:text-brand-300'
            }`}
          >
            {isAny ? t('publicBooking.employee.anyHint') : (employee?.bio ?? '')}
          </div>
        </div>
      </button>

      {/* Miniatury portfolio — tylko dla konkretnego pracownika (nie dla "Dowolny"). */}
      {!isAny && visible.length > 0 ? (
        <div className="mt-3 flex items-center gap-2">
          {visible.map((it, idx) => (
            <button
              key={it.id}
              type="button"
              onClick={(e) => openLightbox(e, idx)}
              className="h-16 w-16 overflow-hidden rounded-md ring-1 ring-zinc-200 transition hover:ring-brand-400 dark:ring-zinc-700"
              aria-label={it.caption ?? t('portfolio.photo')}
            >
              <img
                src={assetUrl(it.thumbnailUrl ?? it.imageUrl)}
                alt={it.caption ?? ''}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
          {more > 0 ? (
            <button
              type="button"
              onClick={(e) => openLightbox(e, previewCount)}
              className="flex h-16 min-w-16 items-center justify-center rounded-md bg-zinc-100 px-3 text-xs font-medium text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:ring-zinc-700 dark:hover:bg-zinc-700"
            >
              {t('portfolio.public.morePhotos', { count: more })}
            </button>
          ) : null}
        </div>
      ) : null}

      {lightboxStart !== null && items.length > 0 ? (
        <GalleryLightbox
          items={items}
          initialIndex={Math.min(lightboxStart, items.length - 1)}
          onClose={() => setLightboxStart(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Krok 3 — slot
// ---------------------------------------------------------------------

function StepSlot({
  selection,
  onBack,
  onNext,
}: {
  selection: Selection;
  onBack: () => void;
  onNext: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState<string>(todayISO());

  const { data, isLoading, error } = useQuery<PublicAvailabilityResponse>({
    queryKey: ['public', 'availability', selection.serviceIds, selection.employeeId, date],
    queryFn: () =>
      fetchAvailability({
        serviceIds: selection.serviceIds,
        employeeId: selection.employeeId ?? undefined,
        date,
      }),
    enabled: selection.serviceIds.length > 0 && date.length === 10,
  });

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{t('publicBooking.slot.title')}</h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.slot.subtitle')}</p>
      </header>

      <DatePickerPopover
        value={date}
        onChange={setDate}
        label={t('publicBooking.slot.datePicker')}
      />

      {isLoading ? <p className="dark:text-zinc-300">{t('publicBooking.loading')}</p> : null}
      {error ? (
        <p className="rounded-lg bg-red-50 p-3 text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
          {publicErrorMessage(error, '')}
        </p>
      ) : null}

      {data && data.slots.length === 0 ? (() => {
        const [yy, mm, dd] = date.split('-').map((n) => Number.parseInt(n, 10));
        const weekday = new Date(yy, (mm ?? 1) - 1, dd ?? 1).getDay();
        const isClosedDay = weekday === 0 || weekday === 1;
        return (
          <div className="rounded-lg bg-white p-4 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800">
            <p className="text-zinc-500 dark:text-zinc-400">
              {isClosedDay
                ? t('publicBooking.slot.salonClosed')
                : t('publicBooking.slot.noSlots')}
            </p>
          </div>
        );
      })() : null}

      {data && data.slots.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {data.slots.map((s) => (
            <button
              key={s.startAt}
              type="button"
              onClick={() =>
                onNext({
                  ...selection,
                  startAt: s.startAt,
                  resolvedEmployee: {
                    id: s.employeeId,
                    firstName: s.employeeFirstName,
                    lastName: s.employeeLastName,
                  },
                })
              }
              className="rounded-md bg-white px-3 py-2 text-sm font-medium text-zinc-800 ring-1 ring-zinc-200 transition hover:border-brand-500 hover:ring-brand-400 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-700 dark:hover:ring-brand-400"
              title={
                selection.employeeId === null
                  ? t('publicBooking.slot.withEmployee', {
                      name: `${s.employeeFirstName} ${s.employeeLastName}`,
                    })
                  : undefined
              }
            >
              {formatTime(s.startAt)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-white px-4 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 transition hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
        >
          {t('publicBooking.back')}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------
// Krok 4 — dane kontaktowe
// ---------------------------------------------------------------------

function StepDetails({
  selection,
  onBack,
  onSuccess,
}: {
  selection: Selection;
  onBack: () => void;
  onSuccess: (r: PublicBookingCreatedResponse) => void;
}) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [rodoConsent, setRodoConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Krok 3 wyłapuje resolvedEmployee — użyjemy go jako employeeId przy POST.
  // (selection.employeeId może być null jeśli klient wybrał "Dowolny".)
  const employeeId = selection.resolvedEmployee?.id;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const next: Record<string, string> = {};
    if (!firstName.trim()) next.firstName = t('publicBooking.details.errors.firstNameRequired');
    if (!phone.trim()) next.phone = t('publicBooking.details.errors.phoneRequired');
    if (!rodoConsent) next.rodoConsent = t('publicBooking.details.errors.rodoRequired');
    if (!smsConsent) next.smsConsent = t('publicBooking.details.errors.smsRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (!selection.startAt || !employeeId) return;

    try {
      setSubmitting(true);
      const res = await createBooking({
        serviceIds: selection.serviceIds,
        employeeId,
        startAt: selection.startAt,
        client: {
          firstName: firstName.trim(),
          lastName: lastName.trim() ? lastName.trim() : null,
          phone: phone.trim(),
          email: email.trim() ? email.trim() : null,
          rodoConsent: true,
          smsConsent: true,
          marketingConsent,
        },
      });
      onSuccess(res);
    } catch (err) {
      setSubmitError(publicErrorMessage(err, t('publicBooking.errorGeneric')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
          {t('publicBooking.details.title')}
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.details.subtitle')}</p>
      </header>

      {/* Podsumowanie wyboru */}
      {selection.startAt && selection.resolvedEmployee ? (
        <div className="rounded-lg bg-brand-50 p-4 ring-1 ring-brand-200 dark:bg-brand-900/30 dark:ring-brand-700">
          <p className="text-xs uppercase tracking-wide text-brand-700 dark:text-brand-300">
            {t('publicBooking.details.summary')}
          </p>
          <p className="mt-1 text-sm font-medium text-brand-900 dark:text-brand-100">
            {formatDateTime(selection.startAt)}
          </p>
          <p className="text-sm text-brand-800 dark:text-brand-200">
            {selection.resolvedEmployee.firstName} {selection.resolvedEmployee.lastName}
          </p>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4 rounded-lg bg-white p-4 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800">
        <Field
          label={t('publicBooking.details.firstName')}
          required
          error={errors.firstName}
        >
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="input"
            autoComplete="given-name"
          />
        </Field>

        <Field label={t('publicBooking.details.lastName')}>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="input"
            autoComplete="family-name"
          />
        </Field>

        <Field
          label={t('publicBooking.details.phone')}
          required
          hint={t('publicBooking.details.phoneHint')}
          error={errors.phone}
        >
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="input"
            autoComplete="tel"
            placeholder="+48 512 345 678"
          />
        </Field>

        <Field label={t('publicBooking.details.email')}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            autoComplete="email"
          />
        </Field>

        <Consent
          checked={rodoConsent}
          onChange={setRodoConsent}
          label={
            <Trans
              i18nKey="publicBooking.details.rodoConsent"
              components={{
                privacy: (
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-700 underline hover:text-brand-800 dark:text-brand-300 dark:hover:text-brand-200"
                  />
                ),
              }}
            />
          }
          hint={t('publicBooking.details.rodoConsentRequired')}
          error={errors.rodoConsent}
        />
        <Consent
          checked={smsConsent}
          onChange={setSmsConsent}
          label={t('publicBooking.details.smsConsent')}
          hint={t('publicBooking.details.smsConsentRequired')}
          error={errors.smsConsent}
        />
        <Consent
          checked={marketingConsent}
          onChange={setMarketingConsent}
          label={t('publicBooking.details.marketingConsent')}
        />

        {submitError ? (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
            {submitError}
          </div>
        ) : null}

        <div className="flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg bg-white px-4 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 transition hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
          >
            {t('publicBooking.back')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-zinc-400 dark:disabled:bg-zinc-700 dark:disabled:text-zinc-500"
          >
            {submitting ? t('publicBooking.submitting') : t('publicBooking.details.submit')}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
        {label}
        {required ? <span className="ml-1 text-red-500 dark:text-red-400">*</span> : null}
      </span>
      <div className="mt-1">{children}</div>
      {hint ? <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{hint}</p> : null}
      {error ? <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
    </label>
  );
}

function Consent({
  checked,
  onChange,
  label,
  hint,
  error,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  hint?: string;
  error?: string;
}) {
  return (
    <div>
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-zinc-600 dark:bg-zinc-800"
        />
        <span className="text-sm text-zinc-700 dark:text-zinc-200">{label}</span>
      </label>
      {hint ? <p className="ml-6 text-xs text-zinc-500 dark:text-zinc-400">{hint}</p> : null}
      {error ? <p className="ml-6 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Krok 5 — sukces
// ---------------------------------------------------------------------

function StepSuccess({
  result,
  onNew,
}: {
  result: PublicBookingCreatedResponse;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const cancelUrl = `/rezerwacja/anuluj/${result.cancelToken}`;
  const rescheduleUrl = `/rezerwacja/przesun/${result.cancelToken}`;
  const fullCancel = `${window.location.origin}${cancelUrl}`;

  // Auto-scroll do góry przy wejściu na success.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="space-y-6">
      <div className="rounded-lg bg-emerald-50 p-6 ring-1 ring-emerald-300 dark:bg-emerald-950/40 dark:ring-emerald-800">
        <h1 className="text-2xl font-semibold text-emerald-900 dark:text-emerald-200">
          {t('publicBooking.success.title')}
        </h1>
        <p className="mt-1 text-sm text-emerald-800 dark:text-emerald-300">{t('publicBooking.success.subtitle')}</p>
      </div>

      <dl className="space-y-3 rounded-lg bg-white p-4 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800">
        <div className="flex justify-between gap-3">
          <dt className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.success.when')}</dt>
          <dd className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            {formatDateTime(result.startAt)}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.success.who')}</dt>
          <dd className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            {result.employee.firstName} {result.employee.lastName}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.success.what')}</dt>
          <dd className="mt-1">
            <ul className="space-y-1 text-sm text-zinc-800 dark:text-zinc-200">
              {result.services.map((s) => (
                <li key={s.id} className="flex justify-between gap-3">
                  <span>{s.name}</span>
                  <span>{formatPriceWithCurrency(s.price)}</span>
                </li>
              ))}
            </ul>
          </dd>
        </div>
        <div className="flex justify-between gap-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          <dt className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('publicBooking.success.total')}</dt>
          <dd className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {formatPriceWithCurrency(result.totalPrice)} · {result.totalDuration} min
          </dd>
        </div>
      </dl>

      <div className="rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-200 dark:bg-zinc-900/50 dark:ring-zinc-800">
        <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {t('publicBooking.success.linkTitle')}
        </h2>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('publicBooking.success.linkHint')}</p>
        <div className="mt-3 break-all rounded-md bg-white px-3 py-2 font-mono text-xs text-zinc-700 ring-1 ring-zinc-200 dark:bg-zinc-950 dark:text-zinc-300 dark:ring-zinc-800">
          {fullCancel}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <a
            href={rescheduleUrl}
            className="rounded-md bg-white px-3 py-2 text-sm text-brand-700 ring-1 ring-brand-300 hover:bg-brand-50 dark:bg-zinc-900 dark:text-brand-300 dark:ring-brand-800 dark:hover:bg-brand-950/40"
          >
            {t('publicBooking.success.linkReschedule')}
          </a>
          <a
            href={cancelUrl}
            className="rounded-md bg-white px-3 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
          >
            {t('publicBooking.success.linkCancel')}
          </a>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onNew}
          className="rounded-lg bg-white px-4 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 transition hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
        >
          {t('publicBooking.success.newBooking')}
        </button>
      </div>
    </section>
  );
}
